import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { Provider } from 'react-redux';
import {
  ThemeProvider,
  StyledEngineProvider,
  experimental_extendTheme as materialExtendTheme,
  THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles';
import { ConfirmationServiceProvider } from 'utils';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { VerifyAuth0Provider } from 'auth0-provider';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { App } from 'App';
import theme from 'styles/customizedTheme';
import store from './store/store';
import 'pdftron/docManager/styles.css';
import '@globalvisioninc/themes/verify/tokens.css';
import 'styles/design-tokens.css';
import { TrackerProviderHook } from 'components';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './queryClient';
import * as Sentry from '@sentry/react';
import { CssVarsProvider, CssVarsProvider as JoyCssVarsProvider } from '@mui/joy/styles';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import MaintenancePage from './pages/MaintenancePage';
import { useFlags } from 'launchdarkly-react-client-sdk';

const isClientFacingEnvironment = import.meta.env.VITE_IS_CLIENT_FACING_ENVIRONMENT === 'true';
const environment = import.meta.env.VITE_ENVIRONMENT;

const PROD_SAMPLE_RATE = 1.0;
const DEV_SAMPLE_RATE = 0.1;

let config = {
  dsn: import.meta.env.VITE_SENTRY_INGEST_EP,
  // Session replay is attached in App, behind the `sentry_replay_integration` flag —
  // LaunchDarkly has no flag values this early.
  integrations: [Sentry.browserProfilingIntegration()],
  profilesSampleRate: isClientFacingEnvironment ? PROD_SAMPLE_RATE : DEV_SAMPLE_RATE,
  // Performance Monitoring
  tracesSampleRate: isClientFacingEnvironment ? PROD_SAMPLE_RATE : DEV_SAMPLE_RATE, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    'localhost',
    'https://verify-val.globalvision.ai',
    'https://verify-api-val.globalvision.ai',
    'https://val-sandbox.globalvision.ai',
    'https://val-sandbox-api.globalvision.ai',
    'https://verify.globalvision.co',
    'https://verify-api.globalvision.co',
    'https://verify-api-staging.globalvision.co',
    'https://verify-api-dev.globalvision.co',
    'https://verify-api-trial.globalvision.co',
    /^https:\/\/verify-.+\.globalvision\.co/,
    /^https:\/\/localhost\/api\/v1/,
    /^https:\/\/verify-pr-.+-web\.globalvision\.vercel\.app/,
    /^https:\/\/verify-pr-.+-api\.globalvision\.vercel\.app/,
  ],
  // Session Replay
  replaysSessionSampleRate: isClientFacingEnvironment ? PROD_SAMPLE_RATE : DEV_SAMPLE_RATE, // Default to 100%, meaning we record everything. If needs to be lowered it can be done so with the env variable.
  replaysOnErrorSampleRate: isClientFacingEnvironment ? PROD_SAMPLE_RATE : DEV_SAMPLE_RATE, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment,
  release: environment ? 'local' : import.meta.env.VERCEL_GIT_COMMIT_SHA,
};

Sentry.init(config);

Sentry.setTag('branchName', import.meta.env.VERCEL_GIT_COMMIT_REF);
Sentry.setTag('gitCommit', import.meta.env.VERCEL_GIT_COMMIT_SHA);

const container = document.getElementById('root');
const root = createRoot(container!);

declare global {
  interface Window {
    analytics: any;
  }
}

const extendedTheme = materialExtendTheme(theme);

// Define a new component to handle the flag logic
const AppOrMaintenance = () => {
  // Use 'maintenance-mode' as the flag key. Adjust if your flag has a different key.
  const featureFlags = useFlags();
  const isMaintenanceMode = featureFlags.maintenanceMode;

  return isMaintenanceMode ? <MaintenancePage /> : <App />;
};

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_ID as string,
    user: {
      // This is very important otherwise every anonymous user would count towars Monthly Active Users in Launch Darkly
      key: 'anon',
    },
  });

  root.render(
    <StyledEngineProvider injectFirst>
      <Provider store={store}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={{ [MATERIAL_THEME_ID]: theme }}>
            <JoyCssVarsProvider defaultMode="dark" colorSchemeSelector="#force_dark_mode">
              <CssVarsProvider theme={extendedTheme}>
                <ConfirmationServiceProvider>
                  <CssBaseline />
                  <LDProvider>
                    <QueryClientProvider client={queryClient}>
                      <BrowserRouter>
                        <VerifyAuth0Provider>
                          <TrackerProviderHook>
                            <AppOrMaintenance />
                            <Analytics />
                            <SpeedInsights />
                          </TrackerProviderHook>
                        </VerifyAuth0Provider>
                      </BrowserRouter>
                      <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                  </LDProvider>
                </ConfirmationServiceProvider>
              </CssVarsProvider>
            </JoyCssVarsProvider>
          </ThemeProvider>
        </StyledEngineProvider>
      </Provider>
    </StyledEngineProvider>,
  );
})();
